import React, { useState, useEffect } from "react";
import { auth, db } from "../firebase-config";
import { doc, deleteDoc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";

export const TeamInfo = ({ teamId, clientObj, onBack, onTeamDeleted }) => {
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState("");

  // Fetch team data from root teams collection
  useEffect(() => {
    if (!teamId) {
      setLoading(false);
      return;
    }

    const fetchTeam = async () => {
      try {
        const teamDoc = await getDoc(doc(db, "teams", teamId));
        if (teamDoc.exists()) {
          setTeam({
            id: teamDoc.id,
            ...teamDoc.data()
          });
        } else {
          setTeam(null);
        }
      } catch (error) {
        console.error("Error fetching team:", error);
        setTeam(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTeam();
  }, [teamId]);

  // Handle loading state
  if (loading) {
    return (
      <div className="team-info">
        <div className="team-info-container">
          <div className="team-info-header">
            <button className="btn-back" onClick={onBack}>
              ← Back
            </button>
            <h2 className="text-2xl font-bold text-white">Team Information</h2>
          </div>
          <div className="no-team-message">
            <p className="text-white">Loading team...</p>
          </div>
        </div>
      </div>
    );
  }

  // Handle case where team is null or undefined
  if (!team) {
    return (
      <div className="team-info">
        <div className="team-info-container">
          <div className="team-info-header">
            <button className="btn-back" onClick={onBack}>
              ← Back
            </button>
            <h2 className="text-2xl font-bold text-white">Team Information</h2>
          </div>
          <div className="no-team-message">
            <p className="text-white">No team found. Please create a team first.</p>
          </div>
        </div>
      </div>
    );
  }

  const handleDeleteTeam = async () => {
    if (!team.id) {
      setDeleteMessage("Error: Team ID not found");
      setTimeout(() => setDeleteMessage(""), 3000);
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete team "${team.teamName}"? This action cannot be undone.`
    );

    if (!confirmDelete) return;

    setIsDeleting(true);
    setDeleteMessage("");

    try {
      // Delete team from root teams collection
      await deleteDoc(doc(db, "teams", team.id));

      // Also remove team from games document
      if (clientObj?.clientId && team.gameId) {
        try {
          const gamesDocRef = doc(db, "users", clientObj.clientId, "games", team.gameId);
          const gamesDoc = await getDoc(gamesDocRef);
          
          if (gamesDoc.exists()) {
            const gamesData = gamesDoc.data();
            const teamsArray = gamesData.teams || [];
            
            // Filter out the deleted team
            const updatedTeams = teamsArray.filter(t => t.teamId !== team.id);
            
            // Update the games document
            await updateDoc(gamesDocRef, {
              teams: updatedTeams,
              lastUpdated: serverTimestamp()
            });
            
            console.log("Team removed from games document");
          }
        } catch (gamesError) {
          console.error("Error removing team from games document:", gamesError);
          // Don't fail the deletion if games document update fails
        }
      }

      setDeleteMessage("Team deleted successfully!");
      
      // Notify parent component that team was deleted
      setTimeout(() => {
        if (onTeamDeleted) {
          onTeamDeleted();
        }
      }, 1500);
    } catch (error) {
      console.error("Error deleting team:", error);
      setDeleteMessage("Error deleting team. Please try again.");
      setIsDeleting(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "Unknown";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-US", { 
      year: "numeric", 
      month: "long", 
      day: "numeric" 
    });
  };

  const isOwner = team.createdBy === auth.currentUser?.uid;

  return (
    <div className="team-info">
      <div className="team-info-container">
        {/* Header */}
        <div className="team-info-header">
          <button className="btn-back" onClick={onBack}>
            ← Back
          </button>
          <h2 className="text-2xl font-bold text-white">Team Information</h2>
        </div>

        {deleteMessage && (
          <div className={`message ${deleteMessage.includes("Error") ? "error" : "success"}`}>
            {deleteMessage}
          </div>
        )}

        {/* Team Details */}
        <div className="team-details">
          {/* Team Avatar and Name */}
          <div className="team-identity">
            <div className="team-avatar-large">
              <span>{team.teamAvatar}</span>
            </div>
            <h3 className="team-name-large">{team.teamName}</h3>
          </div>

          {/* Team Description */}
          {team.teamDescription && (
            <div className="team-section">
              <h4 className="section-title">Description</h4>
              <p className="team-description">{team.teamDescription}</p>
            </div>
          )}

          {/* Team Metadata */}
          <div className="team-section">
            <h4 className="section-title">Team Details</h4>
            <div className="team-meta">
              <div className="meta-item">
                <span className="meta-label">Created:</span>
                <span className="meta-value">{formatDate(team.createdAt)}</span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Game ID:</span>
                <span className="meta-value">{team.gameId || "N/A"}</span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Your Role:</span>
                <span className="meta-value">{isOwner ? "Admin" : "Member"}</span>
              </div>
            </div>
          </div>

          {/* Team Members */}
          <div className="team-section">
            <h4 className="section-title">
              Team Members ({team.teamMembers?.length || 0})
            </h4>
            <div className="members-list">
              {team.teamMembers && team.teamMembers.length > 0 ? (
                team.teamMembers.map((member) => (
                  <div key={member.id} className="member-item">
                    <div className="member-avatar">
                      {member.photoURL ? (
                        <img src={member.photoURL} alt={member.displayName} />
                      ) : (
                        <span className="avatar-placeholder">
                          {(member.displayName || member.email || "?")[0].toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="member-details">
                      <span className="member-name">
                        {member.displayName || member.email || "Unknown"}
                      </span>
                      <span className="member-role">
                        {member.role === "admin" ? "👑 Admin" : "Member"}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="no-members">No members in this team</p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="team-actions">
            {isOwner ? (
              <>
                <button 
                  className="btn-danger"
                  onClick={handleDeleteTeam}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Deleting..." : "Delete Team"}
                </button>
                <p className="action-note">
                  Only the team admin can delete the team
                </p>
              </>
            ) : (
              <p className="action-note">
                You must be the team admin to manage this team
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};